import { type AxiosResponse } from 'axios'
import { baseApi } from 'entities/Auth'
import {
  type IParamsUpdateVoiceCallBySidQualityLog,
  type IParamsUpdateVoicemailDrop,
  type IParamsUpdateVoicemailDropName,
  type IParamsUpdateVoicemailGreetingName,
  type IParamsVoiceCallBySidConference,
  type IParamsVoiceCallBySidConferenceBySid,
  type IParamsVoiceCallHoldUnhold,
  type IParamsVoiceCallTransfer,
  type IParamsVoiceConferenceBySidParticipant,
  type IParamsVoiceRecordStatus,
  type IResponseGetVoicemailDrops,
  type IResponseUpdateVoiceCallBySidQualityLog,
  type IResponseUpdateVoicemailDrop,
  type IResponseUpdateVoicemailDropName,
  type IResponseUploadVoicemailDrops,
  type IResponseVoiceCallByCallSidConference,
  type IResponseVoiceCallBySidConferenceBySid,
  type IResponseVoiceCallCallEnd,
  type IResponseVoiceCallHoldUnhold,
  type IResponseVoiceCallTransfer,
  type IResponseVoiceConferenceBySidEnd,
  type IResponseVoiceConferenceBySidHold,
  type IResponseVoiceConferenceBySidParticipant,
  type IResponseVoiceConferenceBySidResume,
  type IResponseVoicemailGreetings,
  type IResponseVoiceToken,
} from 'entities/Call'

class Api {
  createVoiceToken(): Promise<AxiosResponse<IResponseVoiceToken>> {
    return baseApi.post('voice/token')
  }

  updateVoiceRecordStatus(params: IParamsVoiceRecordStatus): Promise<AxiosResponse<string>> {
    return baseApi.post('voice/record/status', params)
  }

  updateVoiceCallHold(
    params: IParamsVoiceCallHoldUnhold
  ): Promise<AxiosResponse<IResponseVoiceCallHoldUnhold>> {
    return baseApi.post('voice/call/hold', params)
  }

  updateVoiceCallUnhold(
    params: IParamsVoiceCallHoldUnhold
  ): Promise<AxiosResponse<IResponseVoiceCallHoldUnhold>> {
    return baseApi.post('voice/call/unhold', params)
  }

  updateVoiceCallTransfer(
    params: IParamsVoiceCallTransfer
  ): Promise<AxiosResponse<IResponseVoiceCallTransfer>> {
    return baseApi.post('voice/call/transfer', params)
  }

  updateVoiceCallByCallSidConference(
    callSid: string,
    params: IParamsVoiceCallBySidConference
  ): Promise<AxiosResponse<IResponseVoiceCallByCallSidConference>> {
    return baseApi.post(`voice/call/${callSid}/conference`, params)
  }

  updateVoiceCallBySidConferenceBySid(
    callSid: string,
    conferenceSid: string,
    params: IParamsVoiceCallBySidConferenceBySid
  ): Promise<AxiosResponse<IResponseVoiceCallBySidConferenceBySid>> {
    return baseApi.put(`voice/call/${callSid}/conference/${conferenceSid}`, params)
  }

  updateVoiceConferenceBySidParticipant(
    conferenceSid: string,
    params: IParamsVoiceConferenceBySidParticipant
  ): Promise<AxiosResponse<IResponseVoiceConferenceBySidParticipant>> {
    return baseApi.put(`voice/conference/${conferenceSid}/participant`, params)
  }

  updateVoiceConferenceBySidHold(
    conferenceSid: string
  ): Promise<AxiosResponse<IResponseVoiceConferenceBySidHold>> {
    return baseApi.put(`voice/conference/${conferenceSid}/hold`)
  }

  updateVoiceConferenceBySidResume(
    conferenceSid: string
  ): Promise<AxiosResponse<IResponseVoiceConferenceBySidResume>> {
    return baseApi.put(`voice/conference/${conferenceSid}/resume`)
  }

  updateVoiceConferenceBySidEnd(
    conferenceSid: string
  ): Promise<AxiosResponse<IResponseVoiceConferenceBySidEnd>> {
    return baseApi.put(`voice/conference/${conferenceSid}/end`)
  }

  updateVoiceCallCallEnd(callSid: string): Promise<AxiosResponse<IResponseVoiceCallCallEnd>> {
    return baseApi.post('voice/call/call-end', {
      call_sid: callSid,
    })
  }

  updateVoiceCallBySidQualityLog(
    callSid: string,
    params: IParamsUpdateVoiceCallBySidQualityLog
  ): Promise<AxiosResponse<IResponseUpdateVoiceCallBySidQualityLog>> {
    return baseApi.post(`voice/call/${callSid}/quality/log`, params)
  }

  updateVoicemailDrop(
    params: IParamsUpdateVoicemailDrop
  ): Promise<AxiosResponse<IResponseUpdateVoicemailDrop>> {
    return baseApi.post('voice/call/voicemail-drop', params)
  }

  getVoicemailGreetings(): Promise<AxiosResponse<IResponseVoicemailGreetings[]>> {
    return baseApi.get('voice/voicemail/greetings')
  }

  updateVoicemailGreetingName(
    id: number,
    params: IParamsUpdateVoicemailGreetingName
  ): Promise<AxiosResponse<IResponseVoicemailGreetings>> {
    return baseApi.put(`voice/voicemail/greeting/update/${id}`, params)
  }

  deleteVoicemailGreeting(id: number): Promise<AxiosResponse<{ status: string }>> {
    return baseApi.delete(`voice/voicemail/greeting/delete/${id}`)
  }

  uploadVoicemailGreeting(
    data: FormData,
    inboxId: number
  ): Promise<AxiosResponse<IResponseVoicemailGreetings>> {
    return baseApi.post(`voice/voicemail/greeting/upload/${inboxId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  uploadVoicemailDrops(data: FormData): Promise<AxiosResponse<IResponseUploadVoicemailDrops>> {
    return baseApi.post('voice/voicemail/drops/upload', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  getVoicemailDrops(): Promise<AxiosResponse<IResponseGetVoicemailDrops>> {
    return baseApi.get('voice/voicemail/drops')
  }

  deleteVoicemailDrop(id: number): Promise<AxiosResponse<void>> {
    return baseApi.delete(`voice/voicemail/drops/delete/${id}`)
  }

  updateVoicemailDropName(
    id: number,
    params: IParamsUpdateVoicemailDropName
  ): Promise<AxiosResponse<IResponseUpdateVoicemailDropName>> {
    return baseApi.put(`voice/voicemail/drops/update/${id}`, params)
  }
}

export const CallApi = new Api()
